import { useCallback } from "react";

import { useQueryClient } from "react-query";

import { useUser } from "src/contexts/user-context";
import { mentionKey } from "@hightouch/lib/llm/asset-comments/mention-tokens";
import { AGENT_NAME } from "@hightouch/lib/llm/asset-comments/types";
import {
  useReviewAgentsWithSkillsQuery,
  useWorkspaceMembersQuery,
} from "src/graphql";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";

import { type CommentFile, useCommentFile } from "./comment-file";
import type { MentionOption } from "./mention-options-list";

/**
 * Members only. Agents are uncapped: a workspace has a handful, and capping the
 * combined list is what let them evict the members below.
 */
const MEMBER_RESULT_LIMIT = 8;

/**
 * Whether an agent can actually review. A skill can be deleted or reclassified
 * after it is attached, which leaves the agent with nothing to run — the
 * settings section keeps those listed so they can be repaired, but mentioning
 * one here would tag an agent that cannot answer.
 */
export function canReview(agent: {
  skill: { isSkill: boolean } | null;
}): boolean {
  return agent.skill?.isSkill === true;
}

/** Whether an agent answers to what has been typed after the `@`. */
export function matchesQuery(agent: { name: string }, query: string): boolean {
  const needle = query.trim().toLowerCase();

  return needle.length === 0 || agent.name.toLowerCase().includes(needle);
}

/**
 * Which agents a surface offers, and the brand its review agents belong to. A
 * file decides for itself: both agents when its agent answers, none when not.
 */
export function mentionAgentSources({
  file,
  agents,
  brandProfileId,
}: {
  file: CommentFile | null;
  agents: "review" | "hightouch";
  brandProfileId: string | null | undefined;
}): {
  offersAgent: boolean;
  offersReviewAgents: boolean;
  brandProfileId: string | null | undefined;
} {
  return {
    offersAgent: file ? file.hasAgent : agents === "hightouch",
    offersReviewAgents: file ? file.hasAgent : agents === "review",
    brandProfileId: brandProfileId ?? file?.brandProfileId,
  };
}

/**
 * Resolves an `@`-query to workspace members and review agents.
 *
 * Members are fetched per keystroke: a workspace can have thousands of them, so
 * that search has to run server-side. Agents are the opposite — a workspace has
 * a handful, and `reviewAgentsWithSkills` returns the list whole, so it is
 * fetched once and filtered here. Both go through the query cache, so repeat
 * prefixes don't refetch and the agent list is fetched once per session rather
 * than once per keystroke.
 *
 * The two run together rather than in sequence, so the popup doesn't reorder
 * itself under the cursor as a second response lands.
 *
 * Agents require Ad Studio access, matching the settings section that creates them.
 * Workspaces without access skip the agent query because they cannot have review agents.
 *
 * `brandProfileId` scopes the agents to the one the subject belongs to, since
 * an agent belongs to exactly one profile. Omitting it returns every agent in
 * the workspace — for a surface that has no profile to scope by.
 *
 * Projects and tasks offer Hightouch's agent instead, the only agent that
 * answers there. It has no id, so it needs no query. A file whose agent answers
 * its comments offers both, since a review agent's mention goes to that agent
 * too; a file with no agent offers neither.
 */
export const useMentionSearch = ({
  brandProfileId: brandProfileIdArg,
  agents = "review",
}: {
  brandProfileId?: string | null;
  agents?: "review" | "hightouch";
} = {}) => {
  const { offersAgent, offersReviewAgents, brandProfileId } =
    mentionAgentSources({
      file: useCommentFile(),
      agents,
      brandProfileId: brandProfileIdArg,
    });
  const { workspace } = useUser();
  const queryClient = useQueryClient();
  const workspaceId = workspace?.id;

  const isAdStudioEnabled = useIsAdStudioEnabled();

  return useCallback(
    async (query: string): Promise<MentionOption[]> => {
      if (!workspaceId) {
        return [];
      }

      const memberVariables = {
        workspaceId,
        limit: MEMBER_RESULT_LIMIT,
        search: `%${query}%`,
      };

      const agentVariables = brandProfileId ? { brandProfileId } : {};

      const [members, reviewAgents] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: useWorkspaceMembersQuery.getKey(memberVariables),
          queryFn: useWorkspaceMembersQuery.fetcher(memberVariables),
          staleTime: 30_000,
        }),
        isAdStudioEnabled && offersReviewAgents
          ? queryClient.fetchQuery({
              queryKey: useReviewAgentsWithSkillsQuery.getKey(agentVariables),
              queryFn: useReviewAgentsWithSkillsQuery.fetcher(agentVariables),
              staleTime: 30_000,
            })
          : null,
      ]);

      const agentOptions: MentionOption[] = [
        ...(offersAgent && matchesQuery({ name: AGENT_NAME }, query)
          ? [
              {
                kind: "agent" as const,
                value: mentionKey({ kind: "agent" }),
                label: AGENT_NAME,
              },
            ]
          : []),
        ...(reviewAgents?.reviewAgentsWithSkills ?? [])
          .filter((agent) => canReview(agent) && matchesQuery(agent, query))
          .map((agent) => ({
            kind: "reviewAgent" as const,
            reviewAgentId: agent.id,
            value: agent.id,
            label: agent.name,
          })),
      ];

      const userOptions: MentionOption[] = members.all_memberships_v2.flatMap(
        (membership) => {
          const user = membership.user;

          if (!user?.name) {
            return [];
          }

          return [
            {
              kind: "user" as const,
              userId: Number(user.id),
              value: String(user.id),
              label: user.name,
            },
          ];
        },
      );

      // Agents first, which is the order the sections render in. Nothing caps
      // the combined list, so the members keep their own limit rather than
      // giving up a slot per agent.
      return [...agentOptions, ...userOptions];
    },
    [
      queryClient,
      workspaceId,
      isAdStudioEnabled,
      brandProfileId,
      offersAgent,
      offersReviewAgents,
    ],
  );
};
